import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const STAGES = {
  validating: ["Unseen-year test", "#94A3B8"], stress: ["In battery", "#22D3EE"], failed: ["Rejected", "#F87171"],
  final_exam: ["2025+ final exam", "#FBBF24"], proven: ["PROVEN", "#2ECA7F"], failed_holdout: ["Failed 2025+", "#F87171"],
};
export const gbp = (v) => (v == null ? "-" : `£${Math.round(Number(v)).toLocaleString()}`);
const pf = (v) => (v == null ? "-" : Number(v).toFixed(2));
const ok = (v) => (v == null ? "#64748B" : v ? "#2ECA7F" : "#F87171");

export const Row = ({ b, onOpen, extra = null }) => {
  const wf = b.wf || {};
  const [label, color] = STAGES[wf.stage] || [wf.stage || "-", "#94A3B8"];
  const c = wf.checks || {};
  const years = wf.years || {};
  return (
    <tr data-testid={`robust-row-${b.bot_name}`} className="border-b border-[#111827] hover:bg-[#111827] transition-colors">
      <td className="px-3 py-2 text-[#F1F5F9]">
        {onOpen ? (
          <button data-testid={`open-bot-${b.bot_name}`} onClick={() => onOpen(b.bot_name)}
            className="hover:text-[#2ECA7F] hover:underline transition-colors">{b.bot_name}</button>
        ) : b.bot_name}
      </td>
      <td className="px-3 py-2 text-[#94A3B8]">{b.symbol} {b.period}</td>
      <td className="px-3 py-2">{gbp(b.net_profit)} · PF {pf(b.profit_factor)} · {b.total_trades}t</td>
      <td className="px-3 py-2" style={{ color: ok(wf.val ? wf.val_passed : null) }}>
        {wf.no_val ? "n/a (36-mo build)" : wf.val ? `${String(wf.val.backtest_from || "").slice(0, 4)}: ${gbp(wf.val.net_profit)} · PF ${pf(wf.val.profit_factor)}` : "pending"}
      </td>
      <td className="px-3 py-2" style={{ color: ok(c.settings) }}>{wf.stress ? `${wf.stress_ok}/${wf.stress.length} of 6` : "-"}</td>
      <td className="px-3 py-2" style={{ color: ok(c.spread_x2) }}>{wf.x2 ? `${gbp(wf.x2.net_profit)} · PF ${pf(wf.x2.profit_factor)}` : "-"}</td>
      <td className="px-3 py-2" style={{ color: ok(c.years) }}>
        {wf.years ? ["2022", "2023", "2024"].map((y) => `${y.slice(2)}:${years[y] == null ? "-" : gbp(years[y])}`).join(" ") : "-"}
      </td>
      <td className="px-3 py-2" style={{ color: ok(c.monte_carlo) }}>{wf.mc?.dd95 != null ? `${wf.mc.dd95}% (${wf.mc.basis})` : "-"}</td>
      <td className="px-3 py-2" style={{ color: ok(b.oos ? b.oos.passed : null) }}>
        {b.oos ? `${gbp(b.oos.net_profit)} · PF ${pf(b.oos.profit_factor)}` : "-"}
      </td>
      <td className="px-3 py-2 max-w-[260px] truncate" style={{ color }} title={wf.fail_reason || label}>
        {label}{wf.fail_reason ? ` · ${wf.fail_reason}` : ""}
      </td>
      {extra}
    </tr>
  );
};

export const SCORE_HEADERS = ["Bot", "Sym", "Built on", "Unseen year", "Settings", "2x spread", "Years", "MC DD95", "2025+ final", "Verdict"];

export const RobustnessCard = () => {
  const [data, setData] = useState({ counts: {}, bots: [] });
  useEffect(() => {
    api.get("/mt5admin/brain/robustness?limit=40").then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="robustness-card" className="mt-8 border border-[#2ECA7F]/30 rounded-lg overflow-hidden bg-[#0B0D14]">
      <div className="px-5 py-3 border-b border-[#2ECA7F]/30 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-lg font-medium">Robustness pipeline <span className="mono text-[10px] text-[#64748B] uppercase tracking-widest">label only · brain ranks by profit</span></div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">
            unseen year · settings ±20% (4/6) · 2x spread · every year green · Monte Carlo DD95 &lt; 35% · then 2025+ final exam
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="robustness-counts">
          {Object.entries(STAGES).map(([k, [label, color]]) => (
            <span key={k} data-testid={`robust-count-${k}`} className="mono text-[10px] px-2 py-1 rounded border"
              style={{ borderColor: `${color}55`, color }}>{label}: {data.counts?.[k] || 0}</span>
          ))}
        </div>
      </div>
      {data.bots.length === 0 ? (
        <div className="p-6 mono text-xs text-[#64748B]">No candidates yet - bots with PF ≥ 1.15, ≥ 500 trades and DD ≤ 30% enter automatically.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full mono text-[11px] whitespace-nowrap">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#64748B] border-b border-[#1F2937]">
                {SCORE_HEADERS.map((h) => (
                  <th key={h} className="text-left px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{data.bots.map((b) => <Row key={b.bot_name} b={b} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
